import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, List, Map as MapIcon, MapPin, Phone, Mail, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState, EmptyState } from "../../components/common/States";
import { MapView } from "../../components/directory/MapView";
import LocationSearch from "../../components/directory/LocationSearch";
import AgencySheet from "../../components/directory/AgencySheet";
import DirectoryFilters, { EMPTY_FILTERS } from "../../components/directory/DirectoryFilters";

const AgencyLine = ({ a, onOpen }) => (
  <li>
    <button type="button" onClick={() => onOpen(a.id)} className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50" data-testid={`directory-agency-${a.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{a.name}</p>
        <p className="truncate text-xs text-slate-500">{[a.address, a.city, a.country].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5 text-slate-400">
        {a.phone && <Phone className="h-3.5 w-3.5" />}{a.email && <Mail className="h-3.5 w-3.5" />}{a.has_location && <MapPin className="h-3.5 w-3.5 text-blue-600" />}
        {a.distance_km != null && <span className="text-xs font-medium text-slate-600">{a.distance_km} km</span>}
      </div>
    </button>
  </li>
);

const CompanyBlock = ({ c, onOpen }) => (
  <div className="card-surface overflow-hidden" data-testid={`directory-company-${c.slug}`}>
    <div className="flex items-start justify-between gap-3 p-4">
      <div className="min-w-0"><Link to={`/e/${c.slug}?src=annuaire`} className="font-display font-semibold hover:text-blue-700" data-testid={`directory-company-link-${c.slug}`}>{c.name}</Link>
        <p className="text-xs text-slate-500">{c.sector} · {[c.city, c.country].filter(Boolean).join(", ")}</p></div>
      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{c.agency_count} agence(s)</span>
    </div>
    {c.agencies.length > 0 && <ul className="divide-y divide-slate-100 border-t border-slate-100">{c.agencies.map((a) => <AgencyLine key={a.id} a={a} onOpen={onOpen} />)}</ul>}
  </div>
);

export default function Directory() {
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [origin, setOrigin] = useState(null);
  const [view, setView] = useState("liste");
  const [selected, setSelected] = useState(null);
  const qs = useMemo(() => {
    const p = new URLSearchParams(Object.entries(applied).filter(([, v]) => v));
    if (origin) { p.set("lat", origin.latitude); p.set("lng", origin.longitude); }
    return p.toString();
  }, [applied, origin]);
  const { data, loading, error, reload } = useApi(`/public/directory?${qs}`);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <p className="eyebrow">Annuaire</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Trouvez l'organisation et l'agence concernées</h1>
      <p className="mt-2 max-w-2xl text-slate-500">Recherchez par nom, secteur, lieu, agence ou service, consultez les coordonnées puis déposez votre retour.</p>
      <div className="mt-8 space-y-3">
        <DirectoryFilters onApply={setApplied} />
        <div className="card-surface p-3">
          <p className="mb-2 text-xs text-slate-500">Localisation facultative, uniquement pour trier par distance. Rien n'est enregistré.</p>
          <LocationSearch prefix="directory-location" onPick={setOrigin} />
          {origin && <p className="mt-2 flex items-center gap-2 text-xs text-slate-600" data-testid="directory-origin">Tri par distance depuis : <b className="truncate">{origin.label}</b>
            <button type="button" onClick={() => setOrigin(null)} data-testid="directory-origin-clear"><X className="h-3.5 w-3.5" /></button></p>}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500" data-testid="directory-count">{data ? `${data.companies.length} organisation(s) · ${data.total_agencies} agence(s) · ${data.located_agencies} sur la carte` : ""}</p>
        <div className="flex rounded-full border border-slate-200 bg-white p-1">
          <Button size="sm" variant={view === "liste" ? "default" : "ghost"} className="rounded-full" onClick={() => setView("liste")} data-testid="directory-view-list"><List className="mr-1.5 h-4 w-4" />Liste</Button>
          <Button size="sm" variant={view === "carte" ? "default" : "ghost"} className="rounded-full" onClick={() => setView("carte")} data-testid="directory-view-map"><MapIcon className="mr-1.5 h-4 w-4" />Carte</Button>
        </div>
      </div>
      <div className="mt-4">
        {loading && <Loader />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.companies.length && <EmptyState icon={Building2} title="Aucun résultat" text="Modifiez ou réinitialisez vos critères de recherche." />}
        {data && data.companies.length > 0 && view === "liste" && (
          <div className="grid gap-3 md:grid-cols-2" data-testid="directory-list">{data.companies.map((c) => <CompanyBlock key={c.id} c={c} onOpen={setSelected} />)}</div>
        )}
        {data && data.companies.length > 0 && view === "carte" && (
          <div className="grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <MapView points={data.map_points} origin={data.origin} selectedId={selected} onSelect={setSelected} className="h-[380px] lg:h-[560px]" />
              {data.located_agencies < data.total_agencies && <p className="mt-2 text-xs text-slate-500" data-testid="directory-unlocated-note">{data.total_agencies - data.located_agencies} agence(s) sans position GPS renseignée : consultez-les dans la liste.</p>}
            </div>
            <div className="card-surface max-h-[560px] overflow-auto p-4 lg:col-span-2" data-testid="directory-map-panel">
              {selected ? <AgencySheet agencyId={selected} origin={origin} onClose={() => setSelected(null)} /> : (
                data.map_points.length ? <ul className="-mx-3 divide-y divide-slate-100">{data.map_points.map((a) => <AgencyLine key={a.id} a={a} onOpen={setSelected} />)}</ul>
                  : <EmptyState icon={MapPin} title="Aucune agence géolocalisée" text="Aucune agence correspondante n'a encore de position GPS renseignée." />)}
            </div>
          </div>
        )}
      </div>
      <Sheet open={view === "liste" && !!selected} onOpenChange={(o) => { if (!o) { setSelected(null); } }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" aria-describedby={undefined} data-testid="directory-agency-drawer">
          <SheetTitle className="sr-only">Fiche agence</SheetTitle>
          {selected && <AgencySheet agencyId={selected} origin={origin} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
